import { ILayer } from '../layer.type';
import { environment } from '~environments/environment';
import { _ } from '~shared/utils';
import { getSharedLayer } from './shared';
import { TenantConfig } from './tenant-config.type';
import { SymbolLayout, SymbolPaint } from 'mapbox-gl';
import { ValmetDnaTag, ValmetDnaValue } from '~app/custom/neve/valmet-dna.service';
import { sum } from 'lodash';

const markerUrlBase = environment.assetRoot + '/data/T14/markers/';

const smallElementBaseZoom = 13;
const smallElementValveZoom = 12;
const smallElementsOtherZoom = 11;

const valveLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-anchor': 'top',
  'text-radial-offset': 1,
  'text-size': [
    'step', ['zoom'],
    0,
    15, 10,
  ],
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.28,
    16, 0.46,
  ],
};

const valvePaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
  'icon-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
  'text-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
};

const othersLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementValveZoom, 0.45,
    16, 0.8,
  ],
};

const customerLayout: SymbolLayout = {
  'icon-size': [
    'interpolate', ['linear'], ['zoom'],
    smallElementBaseZoom, 0.1,
    16, 0.8,
  ],
};

const textLayout: SymbolLayout = {
  'text-allow-overlap': true,
  'text-field': ['get', 'linkedText'],
  'text-size': 13,
  'text-radial-offset': 1,
  'text-anchor': 'top', // overwritten by some layers
  'text-max-width': 100, // prevent wrapping
};

const textPaint: SymbolPaint = {
  'text-color': 'black',
  'text-halo-color': 'white',
  'text-halo-width': 2,
  'icon-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
  'text-opacity': ['coalesce', ['feature-state', 'opacity'], 1.0],
};

/**
 * Neve
 */
export type TagPair = [position: string, tag: string];

// using this instead of null to enforce a type check
export type EmptyValmetDnaConfiguredTag = {
  isEmpty: true
};

export type ValmetDnaSubtitle = {
  isSubtitle: true,
  text?: string,
};

export function isSubtitle(entry: any): entry is ValmetDnaSubtitle {
  return typeof entry === 'object' && entry.isSubtitle;
}

// rename for clarity
export type ValmetDnaConfiguredTag = ValmetDnaTagConfig;
export type OptionalValmetDnaConfiguredTag = ValmetDnaConfiguredTag | EmptyValmetDnaConfiguredTag;


const defaultValueTransformer = (value: ValmetDnaValue, tag?: ValmetDnaTag): ValmetDnaTransformedValue => {
  const numericValue = +value.value;
  const rounded = numericValue < 100 ? numericValue.toPrecision(2) : numericValue.toFixed(0);
  return {
    ...value,
    numericValue,
    formattedValue: tag ? `${rounded} ${tag.Unit}` : rounded
  }
};

const booleanValueTransformer = (value: ValmetDnaValue, tag?: ValmetDnaTag): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: +value.value,
    formattedValue: value.value === '0' ? 'off' : value.value === '1' ? 'on' : value.value
  }
};

export const isEmptyValmetDnaConfiguredTag = (tag: OptionalValmetDnaConfiguredTag): tag is EmptyValmetDnaConfiguredTag => {
  return !!(tag as any).isEmpty;
};

const configureOptionalTags = (entries: (TagPair | ValmetDnaTagConfig | undefined)[]): (ValmetDnaConfiguredTag | EmptyValmetDnaConfiguredTag)[] => {
  return entries.map(entry => {
    if(!entry) {
      return {
        isEmpty: true
      } as EmptyValmetDnaConfiguredTag
    }

    return configureTag(entry);
  })
}

const configureTagsWithSubtitles = (entries: (TagPair | ValmetDnaTagConfig | ValmetDnaSubtitle)[]): (ValmetDnaConfiguredTag | ValmetDnaSubtitle)[] => {
  return entries.map(entry => {
    return isSubtitle(entry) ? entry : configureTag(entry);
  });
}

const configureTags = (entries: (TagPair | ValmetDnaTagConfig)[]): (ValmetDnaConfiguredTag)[] => {
  return entries.map(entry => configureTag(entry));
};

const configureTag = (entry: (TagPair | ValmetDnaTagConfig)): ValmetDnaConfiguredTag => {
  if(Array.isArray(entry)) {
    return {
      tagpair: entry,
      valueTransformer: defaultValueTransformer
    }
  };

  return entry;
};

export type ValmetDnaTransformedValue = ValmetDnaValue & {
  numericValue: number
  formattedValue?: string
};

export type ValmetDnaTagConfig = {
  tagpair: TagPair,
  valueTransformer?: (value: ValmetDnaValue, tag?: ValmetDnaTag) => ValmetDnaTransformedValue,
  minValue?: number;
  maxValue?: number;
};

const negativeValueTransformer = (value): ValmetDnaTransformedValue => {
  return {
    ...value,
    numericValue: -1 * (+value.value)
  }
};

export interface NeveTenantConfig extends TenantConfig {
  tenantCustomData: {
    /**
     * pressure.return, temperature.* tags are in the order specified in https://miproagile.atlassian.net/browse/MR-865
     * if order is changed, neve-measurements-table will show tags for wrong map locations!
     */
    valmetDnaTags: {
      siloSurface: ValmetDnaTagConfig[],
      pressure: {
        /**
         * Paine-ero
         */
        delta: OptionalValmetDnaConfiguredTag[],
        /**
         * Menopaine
         */
        out: OptionalValmetDnaConfiguredTag[],
        /**
         * Paluupaine
         */
        return: OptionalValmetDnaConfiguredTag[],
        /** used in "Erillisverkko" tab */
        auxiliaryDelta: ValmetDnaTagConfig[],
      },
      temperature: {
        /**
         * Menolämpö
         */
        out: OptionalValmetDnaConfiguredTag[],
        /**
         * Paluulämpö
         */
        return: OptionalValmetDnaConfiguredTag[],
        /** used in "Erillisverkko" tab */
        auxiliaryOut: ValmetDnaTagConfig[],
      },
      power: {
        mainNetwork: (ValmetDnaTagConfig | ValmetDnaSubtitle)[],
        auxiliaryNetwork: ValmetDnaTagConfig[],
        allNetworks: ValmetDnaTagConfig[],
      },
      electricity: ValmetDnaTagConfig[],
      outsideTemperature: ValmetDnaTagConfig[],
      pumpStatus: ValmetDnaTagConfig[],
      kpi: ValmetDnaTagConfig[],
    }
  }
};

const getPowerNetworkTags = () => {
  const mainNetwork = configureTagsWithSubtitles([
    ['01NDB10U901', 'av'], // 1NP VESIKATTILA KL-TEHO
    ['NDB10CU010',  'av'], // 2NP VOIMAKATILA KL-TEHO
    ['NDB31CU001',  'av'], // SK-PESURI KL-TEHO
    ['NDE11FU002',  'av'], // KL-AKKU PURKU KL-TEHO
    {
      tagpair: ['NDE11FU001',  'av'], // KL-AKKU LATAUS KL-TEHO
      valueTransformer: negativeValueTransformer
    },
    {
      tagpair: ['RAB20CU010',  'av'], // APUJÄÄHDYTIN ASEMA KL-TEHO
      valueTransformer: negativeValueTransformer
    },

    { isSubtitle: true },

    ['03NDB01CU001.1', 'av'], // 3NP KL-TEHO
    ['04NDA01CU001.1', 'av'], // 4NP KL-TEHO
    ['5NDB20FN001', 'av'], // 5NP ÖLJYKESKUS KL-TEHO
    ['10NPU01',     'av'], // 10NP HILLERINTIE KL-TEHO
    ['11NDB10CU301', 'av'], // 11NP KAIRATIE KL-TEHO
    ['17N20CU001',  'av'], // 17NP NORVATIE KL-TEHO
    ['12NDB10CU001','av'], // 12NP NIVAVAARA KL-TEHO
  ]);

  const auxiliaryNetwork = configureTags([
    ['20NDA30CU001', 'av'], // 20NP MUUROLA
    ['30N10CU001',   'av'], // 30NP YLLÄS
    ['31NDB22CU001', 'av'], // 31NP KOLARI
    ['32NDB10CU001', 'av'], // 32NP SAVUKOSKI
  ]);

  return {
    mainNetwork,
    auxiliaryNetwork,
    allNetworks: (mainNetwork.filter(tag => !isSubtitle(tag)) as ValmetDnaTagConfig[]).concat(auxiliaryNetwork)
  };
}

const getPressureTags = () => {
  // names are in `/src/app/custom/neve/neve-measurements.component.ts`
  const tags = {
    delta: configureOptionalTags([
      ['NDB10CP901', 'av'],

      ['CYG28CP901', 'av'],
      ['RAB12CP902', 'av'],
      ['RAB12CP901', 'av'],
      ['CYG29CP901', 'av'],
      ['UM10PD01', 'av'],

      ['CYG32CP901', 'av'],
      ['41NDB10CP901', 'av'],
      ['41NDA10CP901', 'av'],
      ['12NDB11CP902', 'av'],
      ['12NDB11CP901', 'av'],
      ['12NDB10CP901', 'av'],
      ['42NDB10CP901', 'av'],
      ['42NDB10CP902', 'av'],
      ['17N10CP901', 'av'],
      ['17NDA01CP901', 'av'],
      ['CYG30CP901', 'av'],
      ['CYG36CP901', 'av'],

      undefined, // Voiko tehdä vähennyslaskuna
      undefined, // Voiko tehdä vähennyslaskuna
      ['CYG35CP901', 'av'],
      ['10N10CP901', 'av'],
      ['CYG25CP901', 'av'],

      ['CYG31CP901', 'av'],
      ['CYG33CP901', 'av'],
      undefined, // Positiot vielä epäselvät

      ['CYG34CP901', 'av'],
      ['CYG26CP901', 'av'],

    ]),
    out: configureOptionalTags([
      ['NDA30CP010', 'av'],

      ['CYG28CP001', 'av'],
      ['RAB12CP001', 'av'],
      ['RAB12CP002', 'av'],
      ['CYG29CP001', 'av'],
      ['CYG27CP001', 'av'],

      ['CYG32CP001', 'av'],
      ['41NDA10CP001', 'av'],
      ['41NDA10CP002', 'av'],
      ['12NDA41CP002', 'av'],
      ['12NDA41CP001', 'av'],
      ['12NDA40CP001', 'av'],
      ['42NDA10CP001', 'av'],
      ['42NDA10CP002', 'av'],
      ['17N10CP001', 'av'],
      ['17NDA01CP003', 'av'],
      ['CYG30CP001', 'av'],
      ['CYG36CP001', 'av'],

      ['PUM10P01', 'av'],
      ['PUM10P02', 'av'],
      ['CYG35CP001', 'av'],
      ['10N10CP001', 'av'],
      ['CYG25CP001', 'av'],

      ['CYG31CP001', 'av'],
      ['CYG33CP001', 'av'],
      undefined, // Positiot vielä epäselvät

      ['CYG34CP001', 'av'],
      ['CYG26CP001', 'av'],
    ]),
    return: configureOptionalTags([
      ['NDB10CP010', 'av'],

      ['CYG28CP002', 'av'],
      undefined,
      ['RAB30CP001', 'av'],
      ['CYG29CP002', 'av'],
      ['CYG27CP002', 'av'],

      ['CYG32CP002', 'av'],
      ['41NDB10CP002', 'av'],
      ['41NDB10CP001', 'av'],
      ['12NDB11CP001', 'av'],
      ['12NDB11CP002', 'av'],
      ['12NDB10CP001', 'av'],
      ['42NDB10CP002', 'av'],
      ['42NDB10CP001', 'av'],
      ['17N20CP001', 'av'],
      ['17NDB01CP004', 'av'],
      ['CYG30CP002', 'av'],
      ['CYG36CP002', 'av'],

      ['PUM20P01', 'av'],
      undefined,
      ['CYG35CP002', 'av'],
      ['10N20CP001', 'av'],
      ['CYG25CP002', 'av'],

      ['CYG31CP002', 'av'],
      ['CYG33CP002', 'av'],
      undefined, // Positiot vielä epäselvät

      ['CYG34CP002', 'av'],
      ['CYG26CP002', 'av'],
    ]),
    // used in "Erillisverkko" tab
    auxiliaryDelta: configureTags([
      ['20NDA30CP009', 'av'],
      ['30N90CP901',   'av'],
      ['31NDA30DP001', 'me'],
      ['32NDA12PC126', 'me'],
    ]),
  };
  if (tags.delta.length !== tags.out.length || tags.delta.length !== tags.return.length) {
    console.warn('Pressure tag lists are not of equal length');
  }
  return tags;
};

const getTemperatureTags = () => {
  // names are in `/src/app/custom/neve/neve-measurements.component.ts`
  const tags = {
    out: configureOptionalTags([
      ['1UN10CT007', 'av'],

      ['CYG28CT001', 'av'],
      undefined,
      ['RAB12CT001', 'av'],
      ['CYG29CT001', 'av'],
      ['CYG27CT001', 'av'],

      ['CYG32CT001', 'av'],
      undefined,
      ['41NDA10CT001', 'av'],
      undefined,
      undefined,
      ['12NDA40CT001', 'av'],
      undefined,
      ['42NDA10CT001', 'av'],
      ['17N10CT002', 'av'],
      ['17NDA01CT001', 'av'],
      ['CYG30CT001', 'av'],
      ['CYG36CT001', 'av'],

      ['PUM10T01', 'av'],
      undefined,
      ['CYG35CT001', 'av'],
      ['10N10CT001', 'av'],
      ['CYG25CT001', 'av'],

      ['CYG31CT001', 'av'],
      ['CYG33CT001', 'av'],
      undefined, // Positiot vielä epäselvät

      ['CYG34CT001', 'av'],
      ['CYG26CT001', 'av'],
    ]),
    return: configureOptionalTags([
      ['NDB10CT010', 'av'],

      ['CYG28CT002', 'av'],
      undefined,
      ['UM20CT010', 'av'],
      ['CYG29CT002', 'av'],
      ['CYG27CT002', 'av'],

      ['CYG32CT002', 'av'],
      undefined,
      ['41NDB10CT001', 'av'],
      undefined,
      undefined,
      ['12NDB10CT001', 'av'],
      undefined,
      ['42NDB10CT001', 'av'],
      ['17N20CT002', 'av'],
      ['17NDB01CT002', 'av'],
      ['CYG30CT002', 'av'],
      ['CYG36CT002', 'av'],

      ['PUM20T01', 'av'],
      undefined,
      ['CYG35CT002', 'av'],
      ['10N20CT001', 'av'],
      ['CYG25CT002', 'av'],

      ['CYG31CT002', 'av'],
      ['CYG33CT002', 'av'],
      undefined, // Positiot vielä epäselvät

      ['CYG34CT002', 'av'],
      ['CYG26CT002', 'av'],
    ]),
    // used in "Erillisverkko" tab
    auxiliaryOut: configureTags([
      ['20NDA30CT002', 'av'],
      ['30N10CT002',   'av'],
      ['31NDA12CT002', 'av'],
      ['32NDB10CT001', 'av'],
    ]),
  };
  if (tags.out.length !== tags.return.length) {
    console.warn('Temperature tag lists are not of equal length');
  }
  return tags;
};

const getKpiTags = (): ValmetDnaTagConfig[] => {
  return [
    // Kolari
    {
      //	Tulipesän lämpötila
      tagpair: ['31HNA10CT001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 500,
      maxValue: 800,
    },
    {
      // Tulipesän paine
      tagpair: ['31HNA10CP001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: -80,
      maxValue: -40,
    },
    {
      // Savukaasun happi
      tagpair: ['31HNA11CA001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 4,
      maxValue: 7,
    },
    {
      // KL Paine-ero
      tagpair: ['31NDA30DP001', 'me'],
      valueTransformer: defaultValueTransformer,
      minValue: 1.3,
      maxValue: 2.0,
    },
    {
      // ÖLjypoltin
      tagpair: ['31H20', 's'],
      valueTransformer: booleanValueTransformer,
      minValue: 0,
      maxValue: 0,
    },

    // Ylläs
    {
      // Tulipesän lämpötila 500...800 °C
      tagpair: ['30H01CT001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 500,
      maxValue: 800,
    },
    {
      // Tulipesän paine -80...-40 Pa
      tagpair: ['30H01CP001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: -80,
      maxValue: -40,
    },
    {
      // Savukaasun happi 4...7 %
      tagpair: ['30H40CQ001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 4,
      maxValue: 7,
    },
    {
      // KL Paine-ero 1,3...2,0 bar
      tagpair: ['30N90CP901', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 1.3,
      maxValue: 2.0,
    },
    // {
    //   // Öljypoltin 0
    //   tagpair: ['30X14AH001', 's'],
    //   valueTransformer: booleanValueTransformer,
    //   minValue: 0,
    //   maxValue: 0,
    // },
    // {
    //   // Öljypoltin 0
    //   tagpair: ['30X13AH001', 's'],
    //   valueTransformer: booleanValueTransformer,
    //   minValue: 0,
    //   maxValue: 0,
    // },

    // Savukoski
    {
      // Tulipesän lämpötila 300...800 °C
      tagpair: ['32HLA11TE112', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 300,
      maxValue: 800,
    },
    {
      // Tulipesän alipaine 20...80 Pa
      tagpair: ['32HLA11PC123', 'me'],
      valueTransformer: defaultValueTransformer,
      minValue: 20,
      maxValue: 80,
    },
    {
      // Savukaasun happi 4...16 %
      tagpair: ['32HNA20QC116', 'me'],
      valueTransformer: defaultValueTransformer,
      minValue: 4,
      maxValue: 16,
    },
    {
      // KL Paine-ero 0,8...1,3 bar
      tagpair: ['32NDA12PC126', 'me'],
      valueTransformer: defaultValueTransformer,
      minValue: 0.8,
      maxValue: 1.3,
    },
    {
      // Öljypoltin 0
      tagpair: ['32EGA12AH001', 's'],
      valueTransformer: booleanValueTransformer,
      minValue: 0,
      maxValue: 0,
    },

    // Muurola
    {
      // Tulipesän lämpötila 500...800 °C
      tagpair: ['20NDV10CT001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 500,
      maxValue: 800,
    },
    {
      // Tulipesän alipaine -80...-40 Pa
      tagpair: ['20NDV10CP001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: -80,
      maxValue: -40,
    },
    {
      // Savukaasun happi 2...14 %
      tagpair: ['20HNA10CQ001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 2,
      maxValue: 14,
    },
    {
      // KL Paine-ero 1,3...2,0 bar
      tagpair: ['20NDA30CP009', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 1.3,
      maxValue: 2.0,
    },
    {
      // Öljypoltin 0
      tagpair: ['20CXP10GW001', 's'],
      valueTransformer: booleanValueTransformer,
      minValue: 0,
      maxValue: 0,
    },

    // Nivavaara
    {
      // Tulipesän lämpötila 450...900 °C
      tagpair: ['12NDV01CT001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 450,
      maxValue: 900,
    },
    {
      // Savukaasun happi 4...7 %
      tagpair: ['12HNA10CQ001', 'av'],
      valueTransformer: defaultValueTransformer,
      minValue: 4,
      maxValue: 7,
    },
    {
      // Öljypoltin 0
      tagpair: ['12HHD02GH001', 's'],
      valueTransformer: booleanValueTransformer,
      minValue: 0,
      maxValue: 0,
    },
  ];
};

const getTenantCustomData = (): NeveTenantConfig['tenantCustomData'] => {
  if (getPressureTags().delta.length !== getTemperatureTags().out.length) {
    console.warn('Pressure and temperature tag arrays are not equal length');
  }
  return {
    valmetDnaTags: {
      siloSurface: configureTags([
        ['END71CL001B', 'av'],
        ['ENE20CL005', 'av'],
      ]),
      pressure: getPressureTags(),
      temperature: getTemperatureTags(),
      power: getPowerNetworkTags(),
      electricity: configureTags([
        ['AQ011B67', 'av']
      ]),
      outsideTemperature: configureTags([
        ['1UN10T004', 'av']
      ]),
      pumpStatus: configureTags([
        ['P01D01', 's'],
        ['P01D02', 's'],
        ['RAB12AP001', 's'],
        ['17NDA01P001', 's'],
        ['17NDA01P002', 's'],
        ['02900PU01', 's'],
        ['02900PU02', 's'],
        ['00201PU01', 's'],
        ['00201PU02', 's'],
        ['05000PU03', 's'],
        ['05000PU04', 's'],
        ['41NDA10AP002', 's'], // Saarenkylän pumppaamo
        ['41NDA10AP001', 's'], // Saarenkylän pumppaamo
        ['42NDA10AP001', 's'], // Napapiirin pumppaamo
        ['42NDA10AP002', 's'], // Napapiirin pumppaamo
      ]),
      kpi: getKpiTags(),
    }
  }
}

export default (): NeveTenantConfig => ({
  layerDefinitions: getLayers(),
  layerDefinitionDefaults: {
    metadata: {
      visibleByZoom: 6,
    },
  },
  hasMultipleMarkerModes: false,
  defaultCenter: {
    lat: 66.523,
    lng: 25.751,
  },
  defaultZoom: 10.82,
  images: [
    { id: 'asiakas', url: markerUrlBase + 'asiakas-Asiakas.png' },
    { id: 'asiakas-iso', url: markerUrlBase + 'asiakas-Asiakas_iso.png' },
    { id: 'lampolaitos-bio-on', url: markerUrlBase + 'running-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-on', url: markerUrlBase + 'running-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-on', url: markerUrlBase + 'running-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-on', url: markerUrlBase + 'running-lämpölaitos.png' },
    { id: 'voimalaitos-on', url: markerUrlBase + 'running-Voimalaitos.png' },
    { id: 'lampolaitos-bio-off', url: markerUrlBase + 'gray-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-off', url: markerUrlBase + 'gray-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-off', url: markerUrlBase + 'gray-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-off', url: markerUrlBase + 'gray-lämpölaitos.png' },
    { id: 'voimalaitos-off', url: markerUrlBase + 'gray-Voimalaitos.png' },
    { id: 'lampolaitos-bio-loading', url: markerUrlBase + 'fault-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-loading', url: markerUrlBase + 'fault-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-loading', url: markerUrlBase + 'fault-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-loading', url: markerUrlBase + 'fault-lämpölaitos.png' },
    { id: 'voimalaitos-loading', url: markerUrlBase + 'fault-Voimalaitos.png' },
    { id: 'lampolaitos-bio-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_biokaasu.png' },
    { id: 'lampolaitos-kpa-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_kpa.png' },
    { id: 'lampolaitos-oil-unlinked', url: markerUrlBase + 'tentative-Lämpölaitos_öljy.png' },
    { id: 'lampolaitos-unlinked', url: markerUrlBase + 'tentative-lämpölaitos.png' },
    { id: 'voimalaitos-unlinked', url: markerUrlBase + 'tentative-Voimalaitos.png' },
    { id: 'paineero-on', url: markerUrlBase + 'running-Paineeneromittauspiste.png' },
    { id: 'paineero-off', url: markerUrlBase + 'critical-Paineeneromittauspiste.png' },
    { id: 'paineero-loading', url: markerUrlBase + 'fault-Paineeneromittauspiste.png' },
    { id: 'paineero-unlinked', url: markerUrlBase + 'tentative-Paineeneromittauspiste.png' },
    { id: 'pumppuasema-on', url: markerUrlBase + 'running-Pumppuasema.png' },
    { id: 'pumppuasema-off', url: markerUrlBase + 'gray-Pumppuasema.png' },
    { id: 'pumppuasema-loading', url: markerUrlBase + 'fault-Pumppuasema.png' },
    { id: 'pumppuasema-unlinked', url: markerUrlBase + 'tentative-Pumppuasema.png' },
    { id: 'kpi-inbounds', url: markerUrlBase + 'running-tachometer-right.png' },
    { id: 'kpi-outofbounds', url: markerUrlBase + 'critical-tachometer-left.png' },
    { id: 'kpi-loading', url: markerUrlBase + 'fault-tachometer-left.png' },
    { id: 'kpi-unlinked', url: markerUrlBase + 'tentative-tachometer-right.png' },
    { id: 'kpi-off', url: markerUrlBase + 'gray-tachometer-right.png' },
    { id: 'diff-higher', url: markerUrlBase + 'running-thermometer-high.png' },
    { id: 'diff-equal', url: markerUrlBase + 'running-thermometer-high.png' },
    { id: 'diff-lower', url: markerUrlBase + 'alarm-thermometer-low.png' },
    { id: 'diff-unlinked', url: markerUrlBase + 'tentative-thermometer-low.png' },
    { id: 'diff-loading', url: markerUrlBase + 'fault-thermometer-low.png' },
    { id: 'diff-off', url: markerUrlBase + 'gray-thermometer-low.png' },
  ],
  alarmFilters: [{
    id: 2,
    name: _('Priority'),
    type: 'dropdown-multiple',
    noFiltersShowsAll: true,
    filter_values: [
      {
        name: 'A-kriittiset',
        value: 1,
        boolean: true
      },
      {
        name: 'B-kriittiset',
        value: 2,
        boolean: true
      },
      {
        name: 'C-kriittiset',
        value: 3,
        boolean: true
      }
    ]
  }],
  tenantCustomData: getTenantCustomData(),
  useGlobalFlags: true,
  renderFlagFilteringForLayers: true
});

// vectorURL and vectorSourceLayer are filled in from encrypted-data/T14.json by MapService.addLayer
// numbering for used layers refers to: https://miproagile.atlassian.net/browse/MR-789
export function getLayers(): ILayer[] {
  return [
    // 1. Kaukolämpöverkosto (Julkinen osa)
    {
      id: 'Putki_julkinen',
      vectorSourceLayer: 'Putki_julkinen',
      name: 'Kaukolämpöverkosto',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: [
          'interpolate', ['linear'], ['zoom'],
          0, 0.5,
          10, 1.25,
          22, 4
        ],
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    {
      id: 'Putki_pieni',
      vectorSourceLayer: 'Putki_pieni',
      name: 'Kaukolämpöverkosto (pienet putket)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#ee1786',
        strokeWeight: 1,
        visible: true,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 2. Ei-julkinen verkosto (Kaukolämpöverkoston osa, esim. Laji: DN150/2Mpuk_ei_julkinen)
    {
      id: 'Putki_ei_julkinen',
      vectorSourceLayer: 'Putki_ei_julkinen',
      name: 'Kaukolämpöverkosto (ei-julkinen)',
      orderingWeight: 10,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'line',
        sourceType: 'vector',
        clickable: true,
        strokeColor: '#fd0150',
        strokeWeight: 1,
        visible: false,
      },
      mapboxOptions: {
        paint: {
          'line-gap-width': 2,
          'line-dasharray': [1, 1],
          'line-blur': 1
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 3. Laitokset
    {
      id: 'Syottopiste',
      name: 'Laitokset',
      orderingWeight: 5,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.power.allNetworks.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => {
        const total = sum(measurements.map(({value}) => parseFloat(value)));
        //// suosiola plant MW can be above 100
        return total < 0.01 ? null : `${total < 100 ? total.toFixed(2) : total.toFixed(1)} ${measurements[0].unit}`;
      },
      linkedAuxMeasurements: getTenantCustomData().valmetDnaTags.kpi,
      linkedAuxMeasurementsOverrideVisibilityBasedOnLinkedMeasurements: (measurements) => {
        const total = sum(measurements.map(({value}) => parseFloat(value)));
        return measurements.length > 0 && total >= 0.01;
      },
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: ['concat',
            [
              'case',
              ['==', ['get', 'Laji'], 'Voimalaitos'], 'voimalaitos',
              ['in', 'BIO', ['get', 'Polttoaine']], 'lampolaitos-bio',
              ['in', 'Turve', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'Hake', ['get', 'Polttoaine']], 'lampolaitos-kpa',
              ['in', 'POK', ['get', 'Polttoaine']], 'lampolaitos-oil',
              ['in', 'POR', ['get', 'Polttoaine']], 'lampolaitos-oil',
              'lampolaitos',
            ],
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['>=', ['get', 'linkedMaxValue'], 0.01], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'text-size': 14,
          'icon-size': [
            'interpolate', ['linear'], ['zoom'],
            smallElementValveZoom, 1,
            16, 1.2,
          ],
          'text-radial-offset': 1.5, // compensate for larger icon
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Aseman tai kaivon tunnus',
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
          coordinates: false,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // (siilot)
    {
      id: 'Siilot',
      name: 'Siilot',
      orderingWeight: 7,
      belowRoadLayers: false,
      selectable: false,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.siloSurface.map(i => i.tagpair),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({label, value, unit}) => `${label.replace(/\s+pinta 2$/i, '')}: ${Math.round(parseFloat(value))} ${unit}`
      ).join('\n'),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: false,
        icon: {
          url: markerUrlBase + 'running-Siilo.png',
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...textLayout,
          'text-anchor': [
            'coalesce',
            ['get', 'text-anchor'],
            'left',
          ],
          'icon-size': 0.75,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 4. Pumppaamot
    {
      id: 'Pumppuasema',
      name: 'Pumppaamot',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.pumpStatus.map(i => i.tagpair),
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'pumppuasema',
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['in', '|2|', ['get', 'linkedValues']], 'on',
              'off',
            ]
          ]
        },
        visible: true,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: othersLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 5. Kaivot
    // TODO
    // 6. Porasulkuventtiilit (Löytyy kasettiventtiilin alta)
    {
      id: 'Porasulkuventtiili',
      vectorSourceLayer: 'Porasulkuventtiili',
      name: 'Porasulkuventtiilit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'PS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 7. Kiertolenkit (Löytyy kasettiventtiilin alta)
    {
      id: 'Kiertolenkki',
      vectorSourceLayer: 'Kiertolenkki_sv',
      name: 'Kiertolenkit',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'running-kiertolenkki.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...othersLayout,
        },
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 8. Elementtiventtiilit (alasvetovalikko)
    //    7 alaryhmää (Jäljelle jääneet osat kasettiventtiilin alta kun ottaa pois porasulkuventtiilit ja kiertolenkit).
    {
      id: 'Haaroitusventtiili_kaivossa',
      vectorSourceLayer: 'Haaroitusventtiili_kaivossa',
      name: 'Haaroitusventtiili kaivossa',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'HVK',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Kaivosulkuventtiili',
      vectorSourceLayer: 'Kaivosulkuventtiili',
      name: 'Kaivosulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'KS',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'Sulkuventtiili',
      vectorSourceLayer: 'Sulkuventtiili',
      name: 'Sulkuventtiili',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'S',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_IV',
      vectorSourceLayer: 'SV_IV',
      name: 'SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'SV_TV',
      vectorSourceLayer: 'SV_TV',
      name: 'SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'IV_SV_IV',
      vectorSourceLayer: 'IV_SV_IV',
      name: 'IV+SV+IV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'IV+SV+IV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    }, {
      id: 'TV_SV_TV',
      vectorSourceLayer: 'TV_SV_TV',
      name: 'TV+SV+TV',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          url: markerUrlBase + 'gray-Venttiili.png',
        },
        visible: false,
      },
      mapboxOptions: {
        layout: {
          ...valveLayout,
          'text-field': 'TV+SV+TV',
        },
        paint: valvePaint,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementValveZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
        group: 'Elementtiventtiilit'
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 9. Liittymät
    {
      id: 'Liittyma',
      vectorSourceLayer: 'Liittyma',
      name: 'Liittymät',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      style: {
        layerType: 'symbol',
        sourceType: 'vector',
        clickable: true,
        icon: {
          urlExpression: [
            'case',
            ['>=', ['number', ['get', 'Lämmitettävä rakennustilavuus (m³)'], 0], 100_000], 'asiakas-iso',
            'asiakas',
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        layout: customerLayout,
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'id',
        visibleByZoom: smallElementBaseZoom,
        hoverComponent: 'ElementDetailsHover',
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: { searchable: false },
      useWorker: true,
    },
    // 10. Verkostopisteet
    // TODO
    // 11. Paine-eromittauspisteet (Löytyy Solmupisteen alta, muut solmupisteet ei tarvitse näkyä)
    {
      id: 'Paine_eromittaus',
      name: 'Paine-eromittauspisteet',
      orderingWeight: 1,
      belowRoadLayers: false,
      selectable: true,
      linkable: true,
      linkedMeasurements: getTenantCustomData().valmetDnaTags.pressure.delta.map(i => 'tagpair' in i ? i.tagpair : undefined).filter(i => i),
      linkedMeasurementFormatter: (measurements) => measurements.map(
        ({value, unit}) => `${parseFloat(value).toFixed(1)} ${unit}`
      ).join('\n'),
      linkedDiffMeasurements: getTenantCustomData().valmetDnaTags.temperature.out.map(i => 'tagpair' in i ? i.tagpair : undefined).filter(i => i),
      linkedDiffBaselineMeasurement: ['1UN10CT007', 'av'],
      linkedDiffOffsetProperty: 'temperature-offset',
      linkedDiffDefaultOffset: 10,
      style: {
        layerType: 'symbol',
        sourceType: 'geojson',
        clickable: true,
        icon: {
          urlExpression: [
            'concat',
            'paineero',
            '-',
            [
              'case',
              ['!', ['has', 'linkedStatus']], 'unlinked',
              ['==', ['get', 'linkedStatus'], 'loading'], 'loading',
              ['>=', ['get', 'linkedMaxValue'], 0.5], 'on',
              'off',
            ]
          ]
        },
        visible: false,
      },
      mapboxOptions: {
        paint: textPaint,
        layout: {
          ...othersLayout,
          ...textLayout,
          'text-anchor': [
            'coalesce',
            ['get', 'text-anchor'],
            'bottom',
          ],
        }
      },
      metadata: {
        uniqueBy: 'id',
        labelBy: 'Tunnus',
        hoverComponent: 'ElementDetailsHover',
        visibleByZoom: smallElementsOtherZoom,
        hoverProperties: {
          allProperties: true,
        },
      },
      searchOptions: {
        searchable: true,
        interestingProperties: [{
          key: 'mapping',
          type: 'contains',
        }]
      },
      useWorker: true,
    },
    // 12. Osoitenumerot
    getSharedLayer('houseNumbers'),
    // 13. Pinnanmuodot
    getSharedLayer('elevationContour'),
    getSharedLayer('depthContour'),
    // 14. Vesialueet
    getSharedLayer('groundWaterAreas'), // 14.1 Pohjavesialueet
    getSharedLayer('drainageAreas'), // 14.2 Valuma-alueet
    getSharedLayer('basinOutlets'), // 14.3 Purkupisteet
  ];
}
